/**
 * WMX Curated Gallery
 * Version 2.1.1
 *
 * Compact inspiration carousel:
 * - Loads body, cap, and logo images from Shopify Files.
 * - Displays selected designs in one horizontally scrollable row.
 * - Uses one section-level call to action instead of card buttons.
 */

(function (window, document) {
  'use strict';

  const SELECTOR = '[data-wmx-gallery]';

  function initializeGallery(root) {
    if (!root || root.dataset.galleryInitialized === 'true') {
      return;
    }

    root.dataset.galleryInitialized = 'true';

    const endpoint = String(root.dataset.endpoint || '').trim();
    const minimumDesigns = parsePositiveInteger(
      root.dataset.minimumDesigns,
      1
    );
    const maximumDesigns = parsePositiveInteger(
      root.dataset.maximumDesigns,
      12
    );

    const grid = root.querySelector('[data-gallery-grid]');
    const template = root.querySelector(
      '[data-gallery-card-template]'
    );
    const status = root.querySelector('[data-gallery-status]');
    const previousButton = root.querySelector('[data-gallery-previous]');
    const nextButton = root.querySelector('[data-gallery-next]');
    const assets = getAssetManifest(root);

    if (!endpoint || !grid || !template || !assets) {
      return;
    }

    fetch(endpoint, {
      method: 'GET',
      redirect: 'follow',
      cache: 'no-store'
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Gallery request failed.');
        }

        return response.json();
      })
      .then(function (payload) {
        const designs =
          payload &&
          payload.success === true &&
          Array.isArray(payload.designs)
            ? payload.designs
            : [];

        const validDesigns = designs
          .filter(isValidDesign)
          .slice(0, maximumDesigns);

        if (validDesigns.length < minimumDesigns) {
          root.remove();
          return;
        }

        validDesigns.forEach(function (design) {
          grid.appendChild(
            createCard(template, design, assets)
          );
        });

        root.classList.add('is-ready');
        initializeCarousel(
          grid,
          previousButton,
          nextButton
        );
      })
      .catch(function (error) {
        console.error('WMX gallery failed:', error);
        root.remove();

        if (status) {
          status.hidden = true;
        }
      });
  }

  function createCard(template, design, assets) {
    const fragment = template.content.cloneNode(true);
    const card = fragment.querySelector('.wmx-gallery-card');
    const label = fragment.querySelector('[data-card-label]');
    const bodyColor = getColor(design, 'body');
    const capColor = getColor(design, 'cap');
    const logoColor = getColor(design, 'logos');

    setLayerImage(
      fragment,
      'body',
      getLayerSource(assets, bodyColor, 'body')
    );
    setLayerImage(
      fragment,
      'cap',
      getLayerSource(assets, capColor, 'cap')
    );
    setLayerImage(
      fragment,
      'logo',
      getLayerSource(assets, logoColor, 'logo')
    );

    setText(fragment, '[data-body-name]', bodyColor.name);
    setText(fragment, '[data-cap-name]', capColor.name);
    setText(fragment, '[data-logo-name]', logoColor.name);

    setSwatch(fragment, '[data-body-swatch]', bodyColor.hex);
    setSwatch(fragment, '[data-cap-swatch]', capColor.hex);
    setSwatch(fragment, '[data-logo-swatch]', logoColor.hex);

    const galleryLabel = String(
      design.galleryLabel || ''
    ).trim();

    if (galleryLabel) {
      label.textContent = galleryLabel;
      label.hidden = false;
    } else {
      label.hidden = true;
      label.textContent = '';
    }

    card.dataset.submissionId = design.submissionId;

    return fragment;
  }

  function getColor(design, key) {
    const designElement =
      design &&
      design.design &&
      design.design.elements &&
      design.design.elements[key]
        ? design.design.elements[key]
        : {};

    const fallback = {
      body: {
        name: design.bodyColor,
        code: design.bodyCode
      },
      cap: {
        name: design.capColor,
        code: design.capCode
      },
      logos: {
        name: design.logoColor,
        code: design.logoCode
      }
    }[key] || {};

    return {
      name: String(
        designElement.name ||
        fallback.name ||
        ''
      ).trim(),
      code: String(
        designElement.code ||
        fallback.code ||
        ''
      ).trim(),
      hex: normalizeHex(
        designElement.hex ||
        '#f2f2f0'
      )
    };
  }

  function getAssetManifest(root) {
    const sectionId = String(root.id || '')
      .replace(/^wmx-gallery-/, '');

    const script = document.querySelector(
      `[data-wmx-gallery-assets="${sectionId}"]`
    );

    if (!script) {
      console.error('WMX gallery asset manifest was not found.');
      return null;
    }

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('WMX gallery asset manifest is invalid:', error);
      return null;
    }
  }

  function getLayerSource(assets, color, layer) {
    const slug = getColorSlug(color?.name);
    const selected =
      slug &&
      assets.colors &&
      assets.colors[slug] &&
      assets.colors[slug][layer]
        ? assets.colors[slug][layer]
        : '';

    return selected || assets.blank?.[layer] || '';
  }

function getColorSlug(name) {
  const value = String(name || '')
    .trim()
    .toLowerCase();

  const aliases = {
    zentler: 'zenler',
    pistachio: 'pistachi'
  };

  if (aliases[value]) {
    return aliases[value];
  }

  if (!value) {
    return '';
  }

  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

  function setLayerImage(root, layer, source) {
    const image = root.querySelector(
      `[data-card-layer="${layer}"]`
    );

    if (image) {
      image.src = source;
    }
  }

  function normalizeHex(value) {
    const hex = String(value || '').trim();

    return /^#[0-9a-f]{6}$/i.test(hex)
      ? hex
      : '#f2f2f0';
  }

  function isValidDesign(design) {
    return Boolean(
      design &&
      design.submissionId &&
      design.product &&
      design.bodyColor &&
      design.capColor &&
      design.logoColor
    );
  }

  function addDesignParameter(baseUrl, submissionId) {
    try {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('design', submissionId);
      return url.toString();
    } catch (error) {
      const separator = baseUrl.includes('?') ? '&' : '?';

      return (
        baseUrl +
        separator +
        'design=' +
        encodeURIComponent(submissionId)
      );
    }
  }

  function initializeCarousel(
    track,
    previousButton,
    nextButton
  ) {
    if (!track || !previousButton || !nextButton) {
      return;
    }

    function getScrollAmount() {
      const firstCard = track.querySelector('.wmx-gallery-card');

      if (!firstCard) {
        return track.clientWidth;
      }

      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;

      return firstCard.getBoundingClientRect().width + gap;
    }

    function updateButtons() {
      const maximumScroll =
        track.scrollWidth - track.clientWidth - 2;

      previousButton.disabled = track.scrollLeft <= 2;
      nextButton.disabled =
        track.scrollLeft >= maximumScroll;
    }

    previousButton.addEventListener('click', function () {
      track.scrollBy({
        left: -getScrollAmount(),
        behavior: 'smooth'
      });
    });

    nextButton.addEventListener('click', function () {
      track.scrollBy({
        left: getScrollAmount(),
        behavior: 'smooth'
      });
    });

    track.addEventListener('scroll', updateButtons, {
      passive: true
    });

    window.addEventListener('resize', updateButtons);

    updateButtons();
  }

  function setText(root, selector, value) {
    const element = root.querySelector(selector);

    if (element) {
      element.textContent = value;
    }
  }

  function setSwatch(root, selector, value) {
    const element = root.querySelector(selector);

    if (element) {
      element.style.setProperty(
        '--swatch-color',
        value
      );
    }
  }

  function parsePositiveInteger(value, fallback) {
    const parsed = Number.parseInt(value, 10);

    return Number.isFinite(parsed) && parsed > 0
      ? parsed
      : fallback;
  }

  function initializeAll() {
    document.querySelectorAll(SELECTOR).forEach(
      initializeGallery
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAll,
      { once: true }
    );
  } else {
    initializeAll();
  }

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      const root =
        event.target.matches &&
        event.target.matches(SELECTOR)
          ? event.target
          : event.target.querySelector(SELECTOR);

      if (root) {
        initializeGallery(root);
      }
    }
  );
})(window, document);
